// Helper function to extract sport from event name
export const extractSportFromEventName = (eventName) => {
  if (!eventName) return 'Unknown';

  // Look for NFL team names
  const nflTeams = ['vikings', 'steelers', 'patriots', 'cowboys', 'packers', 'chiefs',
                    'bills', 'dolphins', 'jets', 'ravens', 'browns', 'bengals',
                    'titans', 'colts', 'jaguars', 'texans', 'broncos', 'chargers',
                    'raiders', 'rams', '49ers', 'seahawks', 'cardinals',
                    'giants', 'eagles', 'commanders', 'cowboys', 'lions', 'bears',
                    'saints', 'falcons', 'panthers', 'buccaneers'];

  const eventNameLower = eventName.toLowerCase();
  if (nflTeams.some(team => eventNameLower.includes(team))) {
    return 'NFL';
  }

  // Add more sport detection logic as needed
  return 'Unknown';
};

// Helper function to determine if two selections are opposite outcomes
const areOppositeOutcomes = (label1, label2) => {
  const l1 = label1.toLowerCase().trim();
  const l2 = label2.toLowerCase().trim();
  
  // If labels are identical, they're definitely not opposite
  if (l1 === l2) return false;
  
  // Check for Over/Under patterns
  if ((l1.includes('over') && l2.includes('under')) || 
      (l1.includes('under') && l2.includes('over'))) {
    return true;
  }
  
  // Check for Yes/No patterns
  if ((l1.includes('yes') && l2.includes('no')) || 
      (l1.includes('no') && l2.includes('yes'))) {
    return true;
  }
  
  // Check for team win patterns (different teams winning)
  const teamWords = ['win', 'wins', 'victory', 'moneyline'];
  const hasTeamWinPattern = teamWords.some(word => l1.includes(word) && l2.includes(word));
  
  if (hasTeamWinPattern && l1 !== l2) {
    return true;
  }
  
  // Check for Home/Away patterns
  if ((l1.includes('home') && l2.includes('away')) || 
      (l1.includes('away') && l2.includes('home'))) {
    return true;
  }
  
  // More aggressive check: if labels are different and contain team names or competitive outcomes
  // This catches cases where team names might be structured differently
  const competitiveWords = ['team', 'player', 'side', 'score', 'points'];
  const hasCompetitivePattern = competitiveWords.some(word => l1.includes(word) || l2.includes(word));
  
  if (hasCompetitivePattern && l1 !== l2) {
    return true;
  }
  
  // Very aggressive check: if the labels are simply different and neither contains common betting terms
  // that would indicate they're variations of the same bet
  const sameOutcomeIndicators = ['total', 'spread', 'handicap', 'margin'];
  const hasSameOutcomePattern = sameOutcomeIndicators.some(word => l1.includes(word) && l2.includes(word));
  
  // If they don't have same outcome patterns and are different, likely opposite outcomes
  if (!hasSameOutcomePattern && l1 !== l2) {
    return true;
  }
  
  return false;
};

// Helper function to check if odds represent a good arbitrage opportunity regardless of outcome detection
const isGoodArbitrageOdds = (odds1, odds2) => {
  const americanToImpliedProb = (odds) => {
    const oddsNum = parseInt(odds);
    if (isNaN(oddsNum)) return null;
    
    if (oddsNum > 0) {
      return 100 / (oddsNum + 100);
    } else {
      return Math.abs(oddsNum) / (Math.abs(oddsNum) + 100);
    }
  };
  
  const prob1 = americanToImpliedProb(odds1);
  const prob2 = americanToImpliedProb(odds2);
  
  if (!prob1 || !prob2) return false;
  
  // Check if combined probability is less than 100% (indicating potential arbitrage)
  const totalProb = prob1 + prob2;
  return totalProb < 1.0; // Less than 100% means arbitrage opportunity
};

// Helper function to calculate arbitrage percentage
export const calculateArbitrage = (dkOdds, fdOdds) => {
  if (!dkOdds || !fdOdds || !Array.isArray(dkOdds) || !Array.isArray(fdOdds) || dkOdds.length === 0 || fdOdds.length === 0) {
    return null;
  }

  try {
    // Convert American odds to implied probabilities
    const americanToImpliedProb = (odds) => {
      const oddsNum = parseInt(odds);
      if (isNaN(oddsNum)) return null;
      
      if (oddsNum > 0) {
        return 100 / (oddsNum + 100);
      } else {
        return Math.abs(oddsNum) / (Math.abs(oddsNum) + 100);
      }
    };

    // Find best arbitrage opportunity across all selections
    let bestArbitrage = null;
    let bestPair = null;

    // First, try to find opposite outcomes for true arbitrage
    dkOdds.forEach(dkSelection => {
      if (!dkSelection || !dkSelection.label || !dkSelection.odds) return;
      
      fdOdds.forEach(fdSelection => {
        if (!fdSelection || !fdSelection.label || !fdSelection.odds) return;
        
        // Check if these are opposite outcomes
        if (areOppositeOutcomes(dkSelection.label, fdSelection.label)) {
          const dkProb = americanToImpliedProb(dkSelection.odds);
          const fdProb = americanToImpliedProb(fdSelection.odds);
          
          if (dkProb === null || fdProb === null) return;
          
          // For opposite outcomes, we're betting on both sides
          // This should theoretically add up to 100% probability
          const totalProb = dkProb + fdProb;
          const arbitrageProfit = (1 - totalProb) * 100;
          
          if (arbitrageProfit > 0 && (!bestArbitrage || arbitrageProfit > bestArbitrage)) {
            bestArbitrage = arbitrageProfit;
            bestPair = {
              dkSelection: dkSelection.label,
              fdSelection: fdSelection.label,
              dkOdds: dkSelection.odds,
              fdOdds: fdSelection.odds,
              dkProb: dkProb * 100,
              fdProb: fdProb * 100,
              profit: arbitrageProfit,
              type: 'opposite'
            };
          }
        }
      });
    });

    // If no opposite outcomes found, fall back to same outcomes (traditional comparison)
    if (!bestPair) {
      dkOdds.forEach(dkSelection => {
        if (!dkSelection || !dkSelection.label || !dkSelection.odds) return;
        
        fdOdds.forEach(fdSelection => {
          if (!fdSelection || !fdSelection.label || !fdSelection.odds) return;
          
          // Try to match selections by label (normalize case and remove extra spaces)
          const dkLabel = dkSelection.label.toLowerCase().trim();
          const fdLabel = fdSelection.label.toLowerCase().trim();
          
          if (dkLabel === fdLabel) {
            const dkProb = americanToImpliedProb(dkSelection.odds);
            const fdProb = americanToImpliedProb(fdSelection.odds);
            
            if (dkProb === null || fdProb === null) return;
            
            // For same outcomes, we're looking for better odds on the same bet
            // This is more about line shopping than true arbitrage
            const priceDifference = Math.abs(dkProb - fdProb) * 100;
            
            if (priceDifference > 0 && (!bestArbitrage || priceDifference > bestArbitrage)) {
              const betterOdds = dkProb < fdProb ? dkSelection.odds : fdSelection.odds;
              const betterPlatform = dkProb < fdProb ? 'DraftKings' : 'FanDuel';
              
              bestArbitrage = priceDifference;
              bestPair = {
                selection: dkSelection.label,
                dkOdds: dkSelection.odds,
                fdOdds: fdSelection.odds,
                dkProb: dkProb * 100,
                fdProb: fdProb * 100,
                betterOdds: betterOdds,
                betterPlatform: betterPlatform,
                priceDifference: priceDifference,
                type: 'same'
              };
            }
          }
        });
      });
    }

    return bestPair;
  } catch (error) {
    console.error('Error calculating arbitrage:', error, { dkOdds, fdOdds });
    return null;
  }
};

// Helper function to get odds for a specific contest
export const getContestOdds = (liveOdds) => (contestId, platform) => {
  if (!liveOdds || !liveOdds.refreshed_data) return null;
  
  const platformKey = platform === 'DraftKings' ? 'draftkings_odds' : 'fanduel_odds';
  const odds = liveOdds.refreshed_data[platformKey];
  
  if (!odds || !Array.isArray(odds)) {
    console.warn(`No ${platform} odds data available`);
    return null;
  }
  
  // Convert contestId to string for comparison since APIs might return different types
  const contestIdStr = String(contestId);
  const contestOdds = odds.find(contest => String(contest.contest_id) === contestIdStr);
  
  if (!contestOdds) {
    console.warn(`No ${platform} selections found for contest ${contestId}`);
    return null;
  }
  
  return contestOdds.selections || null;
};

// Extract available contests from contest pairings API response
export const deriveAvailableContests = (events) => {
  const contests = [];
  events.forEach(event => {
    // Each event has contests array with DraftKings and FanDuel contest data
    if (event.contests && Array.isArray(event.contests)) {
      event.contests.forEach(contest => {
        // Add DraftKings contest
        if (contest.draftkings && contest.draftkings.contest_id) {
          contests.push({
            contestId: contest.draftkings.contest_id,
            platform: 'DraftKings',
            eventName: event.event_name,
            eventDate: event.event_date,
            sportName: event.sport_name || 'Unknown',
            contestType: contest.match_type || 'Unknown'
          });
        }
        // Add FanDuel contest
        if (contest.fanduel && contest.fanduel.contest_id) {
          contests.push({
            contestId: contest.fanduel.contest_id,
            platform: 'FanDuel',
            eventName: event.event_name,
            eventDate: event.event_date,
            sportName: event.sport_name || 'Unknown',
            contestType: contest.match_type || 'Unknown'
          });
        }
      });
    }
  });
  return contests;
};

// Detailed arbitrage calculation for interactive calculator (handles opposite outcomes)
export const calculateDetailedArbitrage = (dkOdds, fdOdds, totalStake = 100, areOpposite = false) => {
  if (!dkOdds || !fdOdds) return null;

  try {
    // Convert American odds to decimal odds
    const americanToDecimal = (odds) => {
      const oddsNum = parseInt(odds);
      if (isNaN(oddsNum)) return null;
      
      if (oddsNum > 0) {
        return (oddsNum / 100) + 1;
      } else {
        return (100 / Math.abs(oddsNum)) + 1;
      }
    };

    // Convert American odds to implied probability
    const americanToImpliedProb = (odds) => {
      const oddsNum = parseInt(odds);
      if (isNaN(oddsNum)) return null;
      
      if (oddsNum > 0) {
        return 100 / (oddsNum + 100);
      } else {
        return Math.abs(oddsNum) / (Math.abs(oddsNum) + 100);
      }
    };

    const dkDecimal = americanToDecimal(dkOdds);
    const fdDecimal = americanToDecimal(fdOdds);
    const dkProb = americanToImpliedProb(dkOdds);
    const fdProb = americanToImpliedProb(fdOdds);

    if (!dkDecimal || !fdDecimal || !dkProb || !fdProb) return null;

    if (areOpposite) {
      // For opposite outcomes (true arbitrage - covering all possibilities)
      const totalImpliedProb = dkProb + fdProb;
      
      // Check if arbitrage exists
      if (totalImpliedProb >= 1) {
        return {
          hasArbitrage: false,
          totalImpliedProb: totalImpliedProb * 100,
          message: 'No arbitrage opportunity - total implied probability is >= 100%',
          type: 'opposite'
        };
      }

      // Calculate arbitrage profit percentage
      const arbitrageProfit = ((1 - totalImpliedProb) * 100);

      // Calculate optimal bet amounts for guaranteed profit
      const dkStake = (totalStake * fdProb) / totalImpliedProb;
      const fdStake = (totalStake * dkProb) / totalImpliedProb;

      // Calculate potential returns
      const dkReturn = dkStake * dkDecimal;
      const fdReturn = fdStake * fdDecimal;

      // Calculate guaranteed profit (both should be equal for true arbitrage)
      const guaranteedProfit = Math.min(dkReturn, fdReturn) - totalStake;

      return {
        hasArbitrage: true,
        arbitrageProfit: arbitrageProfit,
        totalImpliedProb: totalImpliedProb * 100,
        dkImpliedProb: dkProb * 100,
        fdImpliedProb: fdProb * 100,
        dkDecimalOdds: dkDecimal,
        fdDecimalOdds: fdDecimal,
        optimalStakes: {
          total: totalStake,
          draftKings: dkStake,
          fanDuel: fdStake
        },
        potentialReturns: {
          draftKings: dkReturn,
          fanDuel: fdReturn
        },
        guaranteedProfit: guaranteedProfit,
        profitPercentage: (guaranteedProfit / totalStake) * 100,
        type: 'opposite'
      };
    } else {
      // For same outcomes (line shopping - comparing odds for same bet)
      const priceDifference = Math.abs(dkProb - fdProb);
      const betterOdds = dkProb < fdProb ? dkOdds : fdOdds;
      const betterPlatform = dkProb < fdProb ? 'DraftKings' : 'FanDuel';
      const betterDecimal = dkProb < fdProb ? dkDecimal : fdDecimal;
      
      // Calculate value of betting on better odds vs worse odds
      const potentialReturn = totalStake * betterDecimal;
      const extraValue = totalStake * (betterDecimal - (dkProb < fdProb ? fdDecimal : dkDecimal));

      return {
        hasArbitrage: false,
        betterOdds: betterOdds,
        betterPlatform: betterPlatform,
        dkImpliedProb: dkProb * 100,
        fdImpliedProb: fdProb * 100,
        priceDifference: priceDifference * 100,
        extraValue: extraValue,
        potentialReturn: potentialReturn,
        recommendation: `Bet on ${betterPlatform} for better value`,
        type: 'same'
      };
    }
  } catch (error) {
    console.error('Error in detailed arbitrage calculation:', error);
    return null;
  }
};
